import type {
  Blockhash,
  Commitment,
  Connection,
  FetchMiddleware,
  PublicKey,
  Signer,
  TransactionInstruction,
} from '@solana/web3.js'
import { WalletNotConnectedError } from '@solana/wallet-adapter-base'
import {
  ComputeBudgetProgram,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js'
import { base64Encode } from '~/utils'

/**
 * Generic Wallet interface to support any wallet implementation.
 */
type Wallet = {
  publicKey: PublicKey | null
  signTransaction: (transaction: VersionedTransaction) => Promise<VersionedTransaction>
  signAllTransactions?: (transactions: VersionedTransaction[]) => Promise<VersionedTransaction[]>
}

/**
 * Sends and signs a single transaction.
 */
export async function sendTransaction(
  connection: Connection,
  wallet: Wallet,
  instructions: TransactionInstruction[],
  signers: Signer[] = [],
  options: {
    priorityFee?: number
    commitment?: Commitment
  } = {},
): Promise<string> {
  const { priorityFee = 0, commitment = 'confirmed' } = options

  if (!wallet.publicKey) {
    throw new WalletNotConnectedError()
  }

  const { blockhash } = await connection.getLatestBlockhash(commitment)

  const txInstructions = priorityFee > 0
    ? [ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFee }), ...instructions]
    : instructions

  const messageV0 = new TransactionMessage({
    payerKey: wallet.publicKey,
    recentBlockhash: blockhash,
    instructions: txInstructions,
  }).compileToV0Message()

  const transaction = new VersionedTransaction(messageV0)

  if (signers.length > 0) {
    transaction.sign(signers)
  }

  const signedTransaction = await wallet.signTransaction(transaction)
  const rawTransaction = signedTransaction.serialize()

  const txId = await connection.sendRawTransaction(rawTransaction, {
    skipPreflight: true,
    maxRetries: 3,
  })

  console.log('TX Signature:', txId)
  console.log('TX Raw:', base64Encode(rawTransaction))

  return txId
}

type SendTransactionsParams = {
  commitment?: Commitment
  onSuccess?: (txId: string, idx: number) => Promise<void> | void
  onError?: (error: any, idx: number) => Promise<boolean> | boolean
  blockhash?: Blockhash
  maxRetries?: number
  stopOnError?: boolean
  priorityFee?: number
  separate?: boolean
}

/**
 * Sends and signs multiple transactions.
 */
export async function sendTransactions(
  connection: Connection,
  wallet: Wallet,
  instructionSets: TransactionInstruction[][],
  signerSets: Signer[][],
  params: SendTransactionsParams = {},
): Promise<string[]> {
  const {
    commitment = 'confirmed',
    maxRetries = 3,
    onSuccess,
    onError,
    stopOnError = false,
    blockhash,
    priorityFee = 0,
  } = params

  if (!wallet.publicKey) {
    throw new WalletNotConnectedError()
  }

  const recentBlockhash = blockhash || (await connection.getLatestBlockhash(commitment)).blockhash
  const transactions: VersionedTransaction[] = []

  for (const [i, instructions] of instructionSets.entries()) {
    if (!instructions || instructions.length === 0) {
      continue
    }

    const txInstructions = priorityFee > 0
      ? [ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFee }), ...instructions]
      : instructions

    const messageV0 = new TransactionMessage({
      payerKey: wallet.publicKey,
      recentBlockhash,
      instructions: txInstructions,
    }).compileToV0Message()

    const transaction = new VersionedTransaction(messageV0)
    const signers = signerSets[i] || []
    if (signers.length > 0) {
      transaction.sign(signers)
    }
    transactions.push(transaction)
  }

  console.log('Sending Transactions:', transactions.length)

  const signedTransactions = !params.separate && wallet.signAllTransactions
    ? await wallet.signAllTransactions(transactions)
    : await Promise.all(transactions.map(tx => wallet.signTransaction(tx)))

  const results: string[] = []

  for (const [i, signedTransaction] of signedTransactions.entries()) {
    const rawTransaction = signedTransaction!.serialize()

    console.log(`TX(#${i}) Raw:`, base64Encode(rawTransaction))

    try {
      const txId = await connection.sendRawTransaction(rawTransaction, {
        skipPreflight: true,
        maxRetries,
      })

      console.log(`TX(#${i}) Signature:`, txId)

      if (onSuccess) {
        await onSuccess(txId, i)
      }

      results.push(txId)
    } catch (error) {
      console.log(`TX(#${i}) Error:`, error)

      let shouldContinue = true
      if (onError) {
        shouldContinue = await onError(error, i)
      }

      results.push('')

      if (stopOnError && !shouldContinue) {
        break
      }
    }
  }

  return results
}

type ITokenStorage = {
  setToken: (token: string) => void
  getToken: () => string | null
  getTimeSinceLastSet: () => number | null
}

export class LocalTokenStorage implements ITokenStorage {
  private tokenKey = 'auth-token'
  private timeKey = 'last-set'

  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token)
    localStorage.setItem(this.timeKey, String(Date.now()))
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey)
  }

  getTimeSinceLastSet(): number | null {
    const lastSet = localStorage.getItem(this.timeKey)
    return lastSet ? Date.now() - Number(lastSet) : null
  }
}

type TokenAuthFetchMiddlewareArgs = {
  tokenStorage?: ITokenStorage
  tokenExpiry?: number
  getToken: () => Promise<string>
}

/**
 * Middleware to handle token-based authentication in fetch requests.
 */
export function tokenAuthFetchMiddleware({
  tokenStorage = new LocalTokenStorage(),
  tokenExpiry = 5 * 60 * 1000, // 5 minutes
  getToken,
}: TokenAuthFetchMiddlewareArgs): FetchMiddleware {
  return (url: string | URL | Request, options: any, fetch: (...args: any) => void) => {
    (async () => {
      try {
        const token = tokenStorage.getToken()
        const timeSinceLastSet = tokenStorage.getTimeSinceLastSet()
        const tokenIsValid
          = token && token !== 'undefined' && timeSinceLastSet && timeSinceLastSet < tokenExpiry - 10_000
        if (!tokenIsValid) {
          const newToken = await getToken()
          if (newToken) {
            tokenStorage.setToken(newToken)
          }
        }
      } catch (error: any) {
        console.error(error)
      }
      fetch(url, {
        ...options,
        headers: {
          ...(options || {}).headers,
          Authorization: `Bearer ${tokenStorage.getToken()}`,
        },
      })
    })()
  }
}
